<script>
import { mapState, mapActions } from 'pinia';

import { useUserStore } from '../../../store/useUserStore.js';
import { getMyOfficials } from '../api/lobbying';

export default {
  name: 'CpRepresentivesToSelect',
  components: {
    VueSimpleSpinner: require('../vue-simple-spinner').default,
    handleImageError: require('../mixins/utils').default,
    MemberComponent: require('@resources/js/components/member/MemberComponent').default,
  },
  directives: {
    margin: require('../directives/margins').margin,
    marginTop: require('../directives/margins').marginTop,
    marginRight: require('../directives/margins').marginRight,
    marginBottom: require('../directives/margins').marginBottom,
    marginLeft: require('../directives/margins').marginLeft,
  },
  props: {
    handleDialog: Function,
    error: { type: String, default: '' },
  },
  mounted() {
    this.handleMyRepresentatives();
  },
  data() {
    return {
      representatives: [],
      loading: false,
      selectedRepresentativeId: null,
      isDropdownOpen: false,
    };
  },
  computed: {
    ...mapState(useUserStore, ['isUserLogin', 'userProfileData']),
    selectedRepresentative() {
      return this.representatives.find(rep => rep.id === this.selectedRepresentativeId);
    },
  },
  methods: {
    async handleMyRepresentatives() {
      this.loading = true;
      try {
        const response = await getMyOfficials();
        this.representatives = response.officials;
      } catch (error) {
        console.error('Error fetching my representatives:', error);
      } finally {
        this.loading = false;
      }
    },
    selectRepresentative(representativeId) {
      this.selectedRepresentativeId = representativeId;
      this.isDropdownOpen = false;
      const representative = this.representatives.find(rep => rep.id === representativeId);
      this.$emit('representative-selected', representativeId, representative);
    },
    handleSelectChange(event) {
      const representativeId = event.target.value;
      if (representativeId) {
        this.selectRepresentative(representativeId);
      }
    },
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    memberData(rep) {
      return { id: rep.id, image: rep.large_img, fullname: rep.name };
    },
    ...mapActions(useUserStore, ['getUserProfile']),
  },
};
</script>

<template>
  <div class="representativesToSelectContainer">
    <div v-if="loading" class="spinner">
      <vue-simple-spinner />
    </div>
    <div v-else class="representativesContainer">
      <h4 class="heading-2 primary-color" v-margin-top="0">Who you want to contact <span class="caption primary-color">(Required)</span></h4>
      <p class="caption neutral-2-color representativesHelper" v-margin-bottom="0.5">
        Select a representative first — your message will be personalized with their name.
      </p>
      <div class="customSelect" @click="toggleDropdown">
        <div class="selectDisplay" :class="{ selected: selectedRepresentative, 'selectDisplay--error': error }">
          <template v-if="selectedRepresentative">
            <span class="neutral-2-color">{{ selectedRepresentative.name }}</span>
          </template>
          <template v-else>
            <span class="neutral-2-color">- Select -</span>
          </template>
          <span class="selectArrow"><i v-if="isDropdownOpen" class="fas fa-angle-up"></i><i v-else class="fas fa-angle-down"></i></span>
        </div>
        <div v-if="isDropdownOpen" class="dropdownList">
          <div
            v-for="rep in representatives"
            :key="rep.id"
            class="dropdownItem"
            :class="{ selected: selectedRepresentativeId === rep.id }"
            @click.stop="selectRepresentative(rep.id)"
          >
            <MemberComponent :member="memberData(rep)" class="selectMemberComponent" />
          </div>
        </div>
      </div>
      <p v-if="error" class="caption error-color">{{ error }}</p>
    </div>
  </div>
</template>

<style scoped>
.representativesToSelectContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.representativesContainer {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
}
.spinner {
  display: flex;
  align-items: center;
  justify-content: center;
}
.hiddenSelect {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  height: 0;
  width: 0;
}
.customSelect {
  position: relative;
  width: 100%;
}
.selectDisplay {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid var(--neutral-1);
  border-radius: var(--internal-border-radius);
  padding: 8px 12px;
  cursor: pointer;
  background-color: var(--white);
  transition: border-color 0.2s ease;
  gap: 8px;
}
.selectDisplay:hover {
  border-color: var(--primary);
  background-color: var(--primary-1);
}
.selectDisplay--error {
  border-color: var(--error);
}
.selectDisplay--error:hover {
  border-color: var(--error);
  background-color: var(--white);
}
.representativesHelper {
  margin-top: 0;
}
.selectArrow {
  transition: transform 0.2s ease;
  color: var(--neutral-2);
  font-size: 12px;
}
.selectMemberComponent {
  flex: 1;
  min-width: 0;
}
.dropdownList {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  border: 1px solid var(--neutral-1);
  border-radius: 0 0 10px 10px;
  background: var(--white);
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
.dropdownItem {
  padding: 2px 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid var(--neutral-1);
}
.dropdownItem:last-child {
  border-bottom: none;
}
.dropdownItem:hover {
  background-color: var(--primary-1);
}
.dropdownItem.selected {
  background-color: var(--primary-1);
}
</style>
